// Pure helpers behind the app-level sidebar's navigation history (App.js keeps the stack). Opening a sidebar from inside
// another sidebar pushes the one being left; going back pops it; anything opened from outside the sidebar, or a change of the
// URL that showSideBar didn't write, drops the whole stack.

export const MAX_SIDEBAR_HISTORY = 20

// Keys of the sidebar's view state, not of the record it shows: changing them is the same sidebar (contact tab switch), and a
// snapshot keeps them so going back lands on the tab that was open.
export const SIDEBAR_UI_STATE_KEYS = ['sidePanelTab']

// What shows up in a viewProps object without being part of what the sidebar shows (the sidebar's own state spread back
// into showSideBar by tab switches).
const NON_TARGET_KEYS = ['path', 'viewName', 'initialData', 'sidebarProps']

const isBlank = (value) => value === undefined || value === null

const sameValue = (a, b) => String(a) === String(b)

const targetKeys = (viewProps) =>
  Object.keys(viewProps || {}).filter(key => !NON_TARGET_KEYS.includes(key) && !SIDEBAR_UI_STATE_KEYS.includes(key) && !isBlank(viewProps[key]))

// A restorable copy of the sidebar that is open: `sidebarState` is App's { path, viewName, viewProps, initialData, sidebarProps }
// where viewProps is the whole query string, so only the keys the sidebar owns (plus its UI state) are kept — the page behind
// it shares that query string and its filters must not be rolled back. null when no sidebar is showing.
export function snapshotSidebar(sidebarState, ownedKeys = []) {
  if (!sidebarState?.viewName) return null
  const source = sidebarState.viewProps || {}
  const viewProps = {}
  Array.from(new Set([...ownedKeys, ...SIDEBAR_UI_STATE_KEYS])).forEach(key => {
    if (!isBlank(source[key])) viewProps[key] = source[key]
  })
  return {
    path: sidebarState.path,
    viewName: sidebarState.viewName,
    viewProps,
    initialData: sidebarState.initialData,
    sidebarProps: sidebarState.sidebarProps,
  }
}

// Is `next` ({ path, viewName, viewProps }) the sidebar `current` already shows, apart from its UI state? Only keys both sides
// carry are compared, so re-opening the same record with extra context (a conversationKey) still counts as the same sidebar.
export function isSameSidebarTarget(current, next) {
  if (!current || !next) return false
  if (current.path !== next.path || current.viewName !== next.viewName) return false

  const currentKeys = targetKeys(current.viewProps)
  const nextKeys = targetKeys(next.viewProps)
  if (!currentKeys.length && !nextKeys.length) return true

  const common = currentKeys.filter(key => nextKeys.includes(key))
  return common.length > 0 && common.every(key => sameValue(current.viewProps[key], next.viewProps[key]))
}

export function pushEntry(stack, entry, max = MAX_SIDEBAR_HISTORY) {
  if (!entry) return stack
  return [...stack, entry].slice(-max)
}

// Is what the URL says the sidebar is what showSideBar last wrote (`expected`: { path, viewName, viewProps })? Anything else —
// the conversation list rewriting the sidebar params, browser back/forward, a deep link — came from outside. The page's own
// filters in the same query string are not compared, nor is the UI state.
export function matchesExpected(expected, actual) {
  if (!expected || !actual) return false
  if (expected.path !== actual.path || expected.viewName !== actual.viewName) return false
  return targetKeys(expected.viewProps).every(key => sameValue(expected.viewProps[key], actual.viewProps?.[key]))
}
